<template>
  <SignupStepFrame
    :step-number="2"
    :step-total="5"
    eyebrow="Step 2 — Your values"
    :title="titleHtml"
    subtitle="Team members will recognize each other using these values. We've pre-filled common ones — make them yours."
    :can-go-next="isValid"
    @next="onNext"
    @back="$emit('back')"
    @saveAndExit="$emit('saveAndExit')"
  >
    <template #header>
      <div class="wiz-cv__header-row signup-stagger">
        <div class="wiz-cv__header-titles">
          <div class="signup-eyebrow">Step 2 — Core values</div>
          <h1 class="signup-display" v-html="titleHtml"></h1>
          <p class="signup-subtitle">
            Team members will recognize each other using these values. We've pre-filled common ones — make them yours.
          </p>
        </div>
        <div class="value-counter">
          <el-popover
            placement="bottom-end"
            trigger="click"
            popper-class="wiz-cv__suggest-popover"
            :width="240"
            v-model="suggestOpen"
          >
            <a slot="reference" class="wiz-cv__suggest-link">Suggest values</a>
            <div class="wiz-cv__suggest">
              <div class="wiz-cv__suggest-title">Tap to add</div>
              <ul class="wiz-cv__suggest-list">
                <li
                  v-for="s in suggestionsList"
                  :key="s"
                  class="wiz-cv__suggest-item"
                  :class="{
                    'wiz-cv__suggest-item--added': isAlreadyAdded(s),
                    'wiz-cv__suggest-item--disabled': isAlreadyAdded(s) || atMax,
                  }"
                  @click="addSuggestion(s)"
                >
                  <span>{{ s }}</span>
                  <svg
                    v-if="isAlreadyAdded(s)"
                    width="13"
                    height="13"
                    viewBox="0 0 13 13"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 7l2.5 2.5L10 4"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </li>
              </ul>
              <div v-if="atMax" class="wiz-cv__suggest-cap">
                You've reached the {{ maxValues }}-value limit.
              </div>
            </div>
          </el-popover>
          <span class="value-counter__count">{{ values.length }} of {{ maxValues }}</span>
        </div>
      </div>
    </template>

    <div class="wiz-cv">
      <transition-group name="value-list" tag="ul" class="wiz-cv__list">
        <li
          v-for="(v, idx) in values"
          :key="v._key"
          class="wiz-cv__item"
        >
          <input
            class="wiz-cv__input"
            type="text"
            :value="v.name"
            :placeholder="'Core value ' + (idx + 1)"
            maxlength="48"
            @input="onInput(idx, $event.target.value)"
            @keydown.enter.prevent="focusOrAdd(idx)"
          />
          <button
            type="button"
            class="wiz-cv__remove"
            :aria-label="'Remove ' + (v.name || 'value')"
            :disabled="values.length <= 1"
            @click="removeValue(idx)"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <path
                d="M3 3 L11 11 M11 3 L3 11"
                stroke="currentColor"
                stroke-width="1.75"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </li>
      </transition-group>

      <button
        type="button"
        class="wiz-cv__add"
        :disabled="values.length >= maxValues"
        @click="addValue"
      >
        <span aria-hidden="true" class="wiz-cv__plus">+</span>
        Add another value
      </button>

      <p v-if="showMinError" class="wiz-cv__error" role="alert">
        Please add at least {{ minValues }} core values.
      </p>
    </div>
  </SignupStepFrame>
</template>

<script>
import SignupStepFrame from './SignupStepFrame.vue'

/**
 * WizardCoreValuesStep — Step 3 of the self-serve signup wizard.
 *
 * Phase D Track D3 polish: removes the numeric `01/02/...` indices,
 * splits the header into a title group + counter group with a
 * "Suggest values" popover that appends from a curated starter list
 * (Trust, Ownership, Growth, Curiosity, Customer-first, Transparency,
 * Bias for action, Kindness).
 *
 * Behavior:
 *  - Hydrates from $store.state.signup.wizardData.coreValues on created().
 *  - Internal `_key` per row keeps <transition-group> stable on edits.
 *  - On Continue, trims + filters empties and commits SET_CORE_VALUES.
 *  - Enter inside a row jumps focus to the next row or appends.
 */
const MIN = 3
const MAX = 10

const SUGGESTIONS = [
  'Trust',
  'Ownership',
  'Growth',
  'Curiosity',
  'Customer-first',
  'Transparency',
  'Bias for action',
  'Kindness',
]

let _keySeed = 0
function makeKey() {
  _keySeed += 1
  return 'cv-' + _keySeed + '-' + Math.random().toString(36).slice(2, 6)
}

function slugify(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export default {
  name: 'WizardCoreValuesStep',
  components: { SignupStepFrame },
  data() {
    return {
      values: [],
      minValues: MIN,
      maxValues: MAX,
      showMinError: false,
      suggestionsList: SUGGESTIONS,
      suggestOpen: false,
      titleHtml: 'What does <b>great</b> look like on your team?',
    }
  },
  computed: {
    nonEmptyCount() {
      return this.values.filter((v) => String(v.name || '').trim().length > 0).length
    },
    isValid() {
      return this.nonEmptyCount >= MIN
    },
    atMax() {
      return this.values.length >= this.maxValues
    },
    existingNamesLower() {
      const s = new Set()
      this.values.forEach((v) => {
        const n = String(v.name || '').trim().toLowerCase()
        if (n) s.add(n)
      })
      return s
    },
  },
  watch: {
    nonEmptyCount(n) {
      if (n >= MIN) this.showMinError = false
    },
  },
  created() {
    this.hydrateFromStore()
  },
  methods: {
    hydrateFromStore() {
      const s = this.$store && this.$store.state && this.$store.state.signup
      const stored =
        (s && s.wizardData && Array.isArray(s.wizardData.coreValues) && s.wizardData.coreValues) ||
        []
      if (stored.length) {
        this.values = stored.map((cv) => ({
          _key: makeKey(),
          id: (cv && cv.id) || slugify(cv && cv.name) || makeKey(),
          name: (cv && cv.name) || '',
        }))
      } else {
        this.values = [
          { _key: makeKey(), id: 'respect', name: 'Respect' },
          { _key: makeKey(), id: 'integrity', name: 'Integrity' },
          { _key: makeKey(), id: 'teamwork', name: 'Teamwork' },
          { _key: makeKey(), id: 'excellence', name: 'Excellence' },
          { _key: makeKey(), id: 'care', name: 'Care' },
        ]
      }
    },
    onInput(idx, name) {
      if (this.values[idx]) this.values[idx].name = name
    },
    addValue() {
      if (this.values.length >= MAX) return
      this.values.push({ _key: makeKey(), id: makeKey(), name: '' })
      this.$nextTick(() => {
        const inputs = this.$el.querySelectorAll('.wiz-cv__input')
        const last = inputs[inputs.length - 1]
        if (last) last.focus()
      })
    },
    removeValue(idx) {
      if (this.values.length <= 1) return
      this.values.splice(idx, 1)
    },
    focusOrAdd(idx) {
      const inputs = this.$el.querySelectorAll('.wiz-cv__input')
      if (idx + 1 < inputs.length) {
        inputs[idx + 1].focus()
      } else if (this.values.length < MAX) {
        this.addValue()
      }
    },
    isAlreadyAdded(name) {
      return this.existingNamesLower.has(String(name || '').trim().toLowerCase())
    },
    addSuggestion(name) {
      if (this.atMax) return
      if (this.isAlreadyAdded(name)) return
      this.values.push({
        _key: makeKey(),
        id: slugify(name) || makeKey(),
        name: String(name),
      })
      // Close popover if we hit the cap, otherwise keep open for stacking.
      if (this.values.length >= this.maxValues) {
        this.suggestOpen = false
      }
    },
    onNext() {
      const cleaned = this.values
        .map((v) => ({
          id: v.id || slugify(v.name) || makeKey(),
          name: String(v.name || '').trim(),
        }))
        .filter((v) => v.name.length > 0)

      if (cleaned.length < MIN) {
        this.showMinError = true
        return
      }

      this.$store.commit('signup/SET_CORE_VALUES', cleaned)
      this.$emit('next')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './_shared.scss';

/* ── Header row override (split title + counter group) ──
   Padding mirrors `.signup-step__header` since this slot replaces it. */
.wiz-cv__header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 28px 36px 0;
  margin-bottom: 18px;

  @media (max-width: 640px) {
    padding: 22px 22px 0;
    margin-bottom: 14px;
  }
}

.wiz-cv__header-titles {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}

.value-counter {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
  padding-top: 4px;
}

.value-counter__count {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: var(--signup-text-muted);
}

.wiz-cv__suggest-link {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: var(--signup-brand);
  cursor: pointer;
  text-decoration: none;
  transition: color 140ms ease;

  &:hover { color: var(--signup-brand-deep); text-decoration: underline; text-underline-offset: 3px; }
}

@media (max-width: 540px) {
  .wiz-cv__header-row {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  .value-counter {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding-top: 0;
  }
}

/* ── Body list ── */
.wiz-cv {
  display: block;
}

.wiz-cv__list {
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.wiz-cv__item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.wiz-cv__input {
  flex: 1 1 auto;
  min-width: 0;
  height: 48px;
  padding: 0 14px;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--signup-text);
  background: var(--signup-surface);
  border: 1px solid var(--signup-border-soft);
  border-radius: 12px;
  transition: border-color 150ms ease, box-shadow 150ms ease;

  &::placeholder {
    color: #a0a0b5;
    font-weight: 400;
  }

  &:hover:not(:focus) {
    border-color: #d5cde8;
  }

  &:focus {
    outline: none;
    border-color: var(--signup-brand);
    box-shadow: 0 0 0 3px rgba(108, 48, 204, 0.15);
  }
}

.wiz-cv__remove {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--signup-border-soft);
  border-radius: 10px;
  color: var(--signup-text-muted);
  cursor: pointer;
  transition: color 150ms ease, border-color 150ms ease, background 150ms ease;

  &:hover:not(:disabled) {
    color: var(--signup-danger);
    border-color: rgba(204, 48, 48, 0.3);
    background: rgba(204, 48, 48, 0.05);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid var(--signup-gold);
    outline-offset: 2px;
  }
}

.wiz-cv__add {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px dashed rgba(108, 48, 204, 0.35);
  border-radius: 10px;
  padding: 10px 14px;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: var(--signup-brand);
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease, color 150ms ease;

  &:hover:not(:disabled) {
    background: rgba(108, 48, 204, 0.06);
    border-color: var(--signup-brand);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid var(--signup-gold);
    outline-offset: 2px;
  }
}

.wiz-cv__plus {
  font-size: 16px;
  font-weight: 900;
  line-height: 1;
}

.wiz-cv__error {
  margin: 12px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--signup-danger);
}

.value-list-enter-active,
.value-list-leave-active {
  transition: opacity 220ms cubic-bezier(0.22, 1, 0.36, 1),
              transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}
.value-list-enter {
  opacity: 0;
  transform: translateY(-6px);
}
.value-list-leave-to {
  opacity: 0;
  transform: translateX(8px);
}
.value-list-leave-active {
  position: relative;
}
</style>

<!-- Suggest-values popover lives in a teleported el-popover popper, so it
     can't be styled from the scoped block above. Use a global rule keyed
     by the custom popper-class. -->
<style lang="scss">
.wiz-cv__suggest-popover.el-popover {
  padding: 8px;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(30, 10, 60, 0.06), 0 12px 32px -16px rgba(78, 20, 172, 0.18);
  border: 1px solid #ece8f4;
}
.wiz-cv__suggest-popover .wiz-cv__suggest-title {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6b7280;
  padding: 4px 8px 6px;
}
.wiz-cv__suggest-popover .wiz-cv__suggest-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.wiz-cv__suggest-popover .wiz-cv__suggest-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 8px;
  border-radius: 6px;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;
}
.wiz-cv__suggest-popover .wiz-cv__suggest-item:hover {
  background: rgba(108, 48, 204, 0.06);
  color: #6C30CC;
}
.wiz-cv__suggest-popover .wiz-cv__suggest-item--added {
  color: #3AC47D;
}
.wiz-cv__suggest-popover .wiz-cv__suggest-item--added:hover {
  background: rgba(58, 196, 125, 0.08);
  color: #3AC47D;
  cursor: default;
}
.wiz-cv__suggest-popover .wiz-cv__suggest-item--disabled {
  cursor: not-allowed;
  opacity: 0.7;
}
.wiz-cv__suggest-popover .wiz-cv__suggest-item--disabled:hover {
  background: transparent;
}
.wiz-cv__suggest-popover .wiz-cv__suggest-item--added.wiz-cv__suggest-item--disabled:hover {
  background: rgba(58, 196, 125, 0.04);
}
.wiz-cv__suggest-popover .wiz-cv__suggest-cap {
  margin-top: 6px;
  padding: 6px 8px;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  border-top: 1px solid #ece8f4;
}
</style>
